import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '../components/Navbar';
import { TYPOGRAPHY, PLACEHOLDER_IMAGES } from '../constants/typography';

gsap.registerPlugin(ScrollTrigger);

const PartnershipPage = () => {
    // Icon rendering helper
    const renderIcon = (iconName, size = 24) => {
        const icons = {
            building: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/></svg>,
            globe: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
            handshake: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 12h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 14"/><path d="m7 18 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9"/><path d="m2 13 6 6"/></svg>,
            store: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
            fabric: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
            tool: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
            chair: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v3"/><path d="M2 11h20"/><path d="M2 16h20"/><path d="M5 11v10"/><path d="M19 11v10"/></svg>,
            palette: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>,
            package: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
            construction: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
            dollar: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
            box: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>,
            alert: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
            users: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
            trending: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
            target: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
            check: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
            network: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
            book: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
            monitor: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
            message: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
            coffee: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
            art: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
            mic: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>,
            zap: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
            x: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        };
        return icons[iconName] || icons.package;
    };

    useEffect(() => {
        // Animate sections
        gsap.utils.toArray('.animate-section').forEach((section) => {
            gsap.fromTo(section,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    scrollTrigger: {
                        trigger: section,
                        start: 'top 80%',
                        toggleActions: 'play none none reverse'
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, []);

    // Partner categories data
    const partnerCategories = [
        { name: 'Distributors', color: '#C9A96E', icon: 'building' },
        { name: 'Cluster Distributors', color: '#8B7355', icon: 'globe' },
        { name: 'Franchises', color: '#C9A96E', icon: 'handshake' },
        { name: 'Dealers', color: '#8B7355', icon: 'store' }
    ];

    // 73% Solution data
    const solution73Data = [
        { category: 'False Ceiling & POP Works', allocation: '8%', cost: '₹8.00', covered: true },
        { category: 'Painting & Wall Finishes (Texture, Wallpapers)', allocation: '7%', cost: '₹7.00', covered: true },
        { category: 'Flooring (Tiles, Wooden, Stone Inlays)', allocation: '10%', cost: '₹10.00', covered: true },
        { category: 'Modular Kitchen', allocation: '8%', cost: '₹8.00', covered: true },
        { category: 'Wardrobes & Storage Units', allocation: '10%', cost: '₹10.00', covered: true },
        { category: 'Loose Furniture (Beds, Sofas, Dining, etc.)', allocation: '10%', cost: '₹10.00', covered: true },
        { category: 'Soft Furnishing (Curtains, Blinds, Rugs)', allocation: '5%', cost: '₹5.00', covered: true },
        { category: 'Appliances (AC, Chimney, Hob, etc.)', allocation: '5%', cost: '₹5.00', covered: true },
        { category: 'Decor, Art, Mirrors, Accessories', allocation: '4%', cost: '₹4.00', covered: true },
        { category: 'Project Management & Design Fees', allocation: '6%', cost: '₹6.00', covered: true }
    ];

    // Distribution categories
    const distributionCategories = [
        { name: 'Textile Products', icon: 'fabric' },
        { name: 'Hardware & Fittings', icon: 'tool' },
        { name: 'Furniture & Upholstery', icon: 'chair' },
        { name: 'Artifacts & Décor', icon: 'palette' },
        { name: 'Project Imports', icon: 'package' },
        { name: 'False Ceiling & Interior Solutions', icon: 'construction' }
    ];

    // Product range for cluster model
    const productRange = [
        'Laminates', 'MDF', 'Louvers', 'Foam Board', 
        'Acrylic Sheets', 'Furniture', 'Carpets & Curtains',
        'Hardware & Digital Locks', 'Profiles & Doors'
    ];

    return (
        <div style={{ background: '#0A0A0A' }}>
            <Helmet>
                <title>Partnership Opportunities - Hav'n Interiors</title>
                <meta name="description" content="Join India's first Combined Experience Store. Become a distributor, franchise, or dealer with exclusive rights and premium support." />
            </Helmet>

            <Navbar />

            {/* Hero Section */}
            <section style={{
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${PLACEHOLDER_IMAGES.team})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed',
                padding: '180px 60px 100px',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '600px',
                    height: '600px',
                    background: 'radial-gradient(circle, rgba(201, 169, 110, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(80px)'
                }}></div>

                <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <div style={{
                        display: 'inline-flex',
                        padding: '8px 16px',
                        background: 'rgba(201, 169, 110, 0.1)',
                        border: '1px solid rgba(201, 169, 110, 0.3)',
                        borderRadius: '30px',
                        marginBottom: '30px',
                        fontSize: '13px',
                        letterSpacing: '1.5px',
                        textTransform: 'uppercase',
                        color: 'var(--accent-color)',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        {renderIcon('handshake', 18)} Partnership Opportunities
                    </div>

                    <h1 style={{
                        fontSize: 'clamp(48px, 8vw, 92px)',
                        fontWeight: 200,
                        letterSpacing: '-3px',
                        marginBottom: '25px',
                        color: 'var(--text-light)',
                        lineHeight: 1.1
                    }}>
                        Be More Than A<br/>
                        <span style={{
                            background: 'linear-gradient(135deg, #C9A96E 0%, #F5E6D3 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text'
                        }}>Market Leader</span>
                    </h1>

                    <p style={{
                        fontSize: '20px',
                        color: 'rgba(245, 241, 237, 0.7)',
                        maxWidth: '900px',
                        margin: '0 auto 40px',
                        lineHeight: 1.8
                    }}>
                        With exclusive rights, cross-selling advantages, and access to 73% of project requirements,
                        we deliver scale, margins, and global reach.
                    </p>
                </div>
            </section>


            {/* About & Tagline */}
            <section className="animate-section" style={{
                padding: '100px 60px',
                background: 'linear-gradient(180deg, #0A0A0A 0%, #0F0F0F 100%)'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <div style={{
                        textAlign: 'center',
                        padding: '60px',
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        borderRadius: '24px',
                        border: '2px solid var(--accent-color)'
                    }}>
                        <h2 style={{
                            fontSize: '32px',
                            color: 'var(--accent-color)',
                            marginBottom: '30px',
                            fontWeight: 400
                        }}>
                            About Us
                        </h2>
                        <p style={{
                            fontSize: '18px',
                            lineHeight: 1.8,
                            color: 'rgba(245, 241, 237, 0.9)',
                            marginBottom: '30px'
                        }}>
                            We are Chennai's first-of-its-kind Combined 8 Experience Store, bringing together a vast spectrum of 
                            global brands and exclusive Indian collaborations. With strong expertise in CNC Manufacturing, OEM/ODM 
                            partnerships, direct imports, and presence in international projects, we deliver unmatched value to our associates.
                        </p>
                        <div style={{
                            padding: '30px',
                            background: 'rgba(201, 169, 110, 0.1)',
                            borderRadius: '16px',
                            borderLeft: '4px solid var(--accent-color)'
                        }}>
                            <h3 style={{
                                fontSize: '24px',
                                color: 'var(--accent-color)',
                                marginBottom: '15px'
                            }}>
                                Why Partner With Us
                            </h3>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                gap: '20px',
                                textAlign: 'left',
                                marginTop: '20px'
                            }}>
                                <div>
                                    <strong style={{ color: 'var(--accent-color)' }}>Direct Importer & Master Distributor</strong>
                                    <p style={{ color: 'rgba(245, 241, 237, 0.85)', fontSize: '15px', marginTop: '8px' }}>
                                        Exclusive distribution rights for leading global and Indian brands.
                                    </p>
                                </div>
                                <div>
                                    <strong style={{ color: 'var(--accent-color)' }}>Unique Product Designs</strong>
                                    <p style={{ color: 'rgba(245, 241, 237, 0.85)', fontSize: '15px', marginTop: '8px' }}>
                                        Custom-developed concepts from our in-house design expertise.
                                    </p>
                                </div>
                                <div>
                                    <strong style={{ color: 'var(--accent-color)' }}>Cross-Selling Advantage</strong>
                                    <p style={{ color: 'rgba(245, 241, 237, 0.85)', fontSize: '15px', marginTop: '8px' }}>
                                        A ready-made platform enabling distributors to tap into Architects, Interior Designers, 
                                        and Project Consultants for multiple categories.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Distribution Categories */}
            <section className="animate-section" style={{
                padding: '100px 60px',
                background: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${PLACEHOLDER_IMAGES.materials})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <h2 style={{
                        fontSize: 'clamp(36px, 5vw, 56px)',
                        fontWeight: 300,
                        color: '#fff',
                        marginBottom: '20px',
                        textAlign: 'center',
                        letterSpacing: '-1px'
                    }}>
                        Categories Offered Under Distribution
                    </h2>
                    <p style={{
                        textAlign: 'center',
                        color: 'rgba(245, 241, 237, 0.7)',
                        marginBottom: '60px',
                        fontSize: '18px'
                    }}>
                        Each category comes with exclusive rights, premium margins, and strong market demand,
                        creating multi-channel opportunities for growth.
                    </p>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: '30px',
                        maxWidth: '1200px',
                        margin: '0 auto'
                    }}>
                        {distributionCategories.map((cat, idx) => (
                            <div key={idx} style={{
                                padding: '35px',
                                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                borderRadius: '16px',
                                border: '1px solid rgba(201, 169, 110, 0.2)',
                                textAlign: 'center',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                            }}
                            className="category-card"
                            >
                                <div style={{
                                    marginBottom: '20px',
                                    color: 'var(--accent-color)',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}>
                                    {renderIcon(cat.icon, 64)}
                                </div>
                                <h3 style={{
                                    fontSize: '20px',
                                    color: 'var(--text-light)',
                                    fontWeight: 500
                                }}>
                                    {cat.name}
                                </h3>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* 73% Solution */}
            <section className="animate-section" style={{
                padding: '100px 60px',
                background: 'linear-gradient(180deg, #0A0A0A 0%, #0F0F0F 100%)'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <h2 style={{
                            fontSize: 'clamp(36px, 5vw, 56px)',
                            fontWeight: 300,
                            color: '#fff',
                            marginBottom: '20px',
                            letterSpacing: '-1px'
                        }}>
                            The 73% Solution
                        </h2>
                        <p style={{
                            fontSize: '24px',
                            color: 'var(--accent-color)',
                            fontWeight: 500,
                            marginBottom: '15px'
                        }}>
                            A New Standard for Project Success
                        </p>
                        <p style={{
                            fontSize: '18px',
                            color: 'rgba(245, 241, 237, 0.7)',
                            maxWidth: '900px',
                            margin: '0 auto'
                        }}>
                            We empower with a wide coverage of your interior requirements. For a villa project of 4,500 sq.ft. 
                            (₹100 Lakh Budget), here's how the cost allocation works.
                        </p>
                    </div>

                    {/* Table */}
                    <div style={{
                        overflowX: 'auto',
                        background: 'rgba(26, 26, 46, 0.6)',
                        borderRadius: '16px',
                        border: '1px solid rgba(201, 169, 110, 0.3)'
                    }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse'
                        }}>
                            <thead>
                                <tr style={{ background: 'var(--accent-color)' }}>
                                    <th style={{
                                        padding: '20px',
                                        textAlign: 'left',
                                        color: '#000',
                                        fontWeight: 600,
                                        fontSize: '16px'
                                    }}>Category</th>
                                    <th style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: '#000',
                                        fontWeight: 600,
                                        fontSize: '16px'
                                    }}>% Allocation</th>
                                    <th style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: '#000',
                                        fontWeight: 600,
                                        fontSize: '16px'
                                    }}>Cost (₹ in Lakhs)</th>
                                    <th style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: '#000',
                                        fontWeight: 600,
                                        fontSize: '16px'
                                    }}>Our Coverage</th>
                                </tr>
                            </thead>
                            <tbody>
                                {solution73Data.map((item, idx) => (
                                    <tr key={idx} style={{
                                        borderBottom: '1px solid rgba(201, 169, 110, 0.1)',
                                        background: idx % 2 === 0 ? 'rgba(26, 26, 46, 0.3)' : 'transparent'
                                    }}>
                                        <td style={{
                                            padding: '18px 20px',
                                            color: 'rgba(245, 241, 237, 0.9)',
                                            fontSize: '15px'
                                        }}>{item.category}</td>
                                        <td style={{
                                            padding: '18px 20px',
                                            textAlign: 'center',
                                            color: 'var(--accent-color)',
                                            fontSize: '16px',
                                            fontWeight: 600
                                        }}>{item.allocation}</td>
                                        <td style={{
                                            padding: '18px 20px',
                                            textAlign: 'center',
                                            color: 'rgba(245, 241, 237, 0.9)',
                                            fontSize: '15px'
                                        }}>{item.cost}</td>
                                        <td style={{
                                            padding: '18px 20px',
                                            textAlign: 'center',
                                            fontSize: '18px'
                                        }}>
                                            {item.covered ? 
                                                <span style={{ color: '#4ade80', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                                    {renderIcon('check', 16)} We Do
                                                </span> : 
                                                <span style={{ color: '#f87171', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                                                    {renderIcon('x', 16)} Not Covered
                                                </span>
                                            }
                                        </td>
                                    </tr>
                                ))}
                                <tr style={{ background: 'rgba(201, 169, 110, 0.2)', fontWeight: 'bold' }}>
                                    <td style={{
                                        padding: '20px',
                                        color: 'var(--text-light)',
                                        fontSize: '18px',
                                        fontWeight: 600
                                    }}>Total</td>
                                    <td style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: 'var(--accent-color)',
                                        fontSize: '18px',
                                        fontWeight: 600
                                    }}>100%</td>
                                    <td style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: 'var(--text-light)',
                                        fontSize: '18px',
                                        fontWeight: 600
                                    }}>₹100.00 Lakh</td>
                                    <td style={{
                                        padding: '20px',
                                        textAlign: 'center',
                                        color: 'var(--accent-color)',
                                        fontSize: '18px',
                                        fontWeight: 600
                                    }}>73% Covered by Us</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p style={{
                        textAlign: 'center',
                        marginTop: '40px',
                        fontSize: '18px',
                        color: 'var(--accent-color)',
                        fontStyle: 'italic'
                    }}>
                        As your distributor partner, we cover 73% of the project requirements — giving you unmatched 
                        advantage in cross-selling and project execution.
                    </p>
                </div>
            </section>

            {/* Cluster Distributor Model */}
            <section className="animate-section" style={{
                padding: '100px 60px',
                background: '#0F0F0F'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <h2 style={{
                        fontSize: 'clamp(36px, 5vw, 56px)',
                        fontWeight: 300,
                        color: '#fff',
                        marginBottom: '20px',
                        textAlign: 'center',
                        letterSpacing: '-1px'
                    }}>
                        Cluster Distributor Model
                    </h2>
                    <p style={{
                        textAlign: 'center',
                        fontSize: '18px',
                        color: 'rgba(245, 241, 237, 0.7)',
                        marginBottom: '60px',
                        maxWidth: '900px',
                        margin: '0 auto 60px'
                    }}>
                        The First-Ever Cluster Concept in the Industry. Instead of investing heavily in a single category, 
                        we combine 3 to 5 product categories under one cluster distributor.
                    </p>

                    {/* Split Screen Comparison */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                        gap: '40px',
                        marginBottom: '60px'
                    }}>
                        {/* Traditional Distributor */}
                        <div style={{
                            background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
                            padding: '40px',
                            borderRadius: '20px',
                            border: '2px solid rgba(156, 163, 175, 0.3)'
                        }}>
                            <h3 style={{
                                fontSize: '24px',
                                color: '#fff',
                                marginBottom: '25px',
                                textAlign: 'center',
                                fontWeight: 600
                            }}>
                                Traditional Distributor
                            </h3>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0
                            }}>
                                {[
                                    { icon: 'dollar', label: 'Investment', value: '₹50 Lakh → Only 1 Product (Laminates)' },
                                    { icon: 'box', label: 'Inventory Load', value: '5,000 sheets, high storage cost' },
                                    { icon: 'alert', label: 'Risk', value: 'High (market fluctuations affect entire stock)' },
                                    { icon: 'users', label: 'Limited Reach', value: 'Only 1 category, fewer clients' },
                                    { icon: 'trending', label: 'Profit', value: 'Narrow margins, slow growth' }
                                ].map((item, idx) => (
                                    <li key={idx} style={{
                                        padding: '15px',
                                        marginBottom: '12px',
                                        background: 'rgba(0,0,0,0.3)',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px'
                                    }}>
                                        <span style={{ color: '#cbd5e0', flexShrink: 0 }}>{renderIcon(item.icon, 24)}</span>
                                        <div>
                                            <strong style={{ color: '#e2e8f0' }}>{item.label}:</strong>{' '}
                                            <span style={{ color: '#cbd5e0' }}>{item.value}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Cluster Distributor (Our Model) */}
                        <div style={{
                            background: 'linear-gradient(135deg, #d4af37 0%, #aa8c2c 100%)',
                            padding: '40px',
                            borderRadius: '20px',
                            border: '3px solid var(--accent-color)',
                            boxShadow: '0 0 40px rgba(201, 169, 110, 0.3)',
                            position: 'relative'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: '-15px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: '#4ade80',
                                color: '#000',
                                padding: '8px 20px',
                                borderRadius: '20px',
                                fontSize: '14px',
                                fontWeight: 600,
                                letterSpacing: '1px'
                            }}>
                                ⭐ RECOMMENDED
                            </div>
                            <h3 style={{
                                fontSize: '24px',
                                color: '#000',
                                marginBottom: '25px',
                                textAlign: 'center',
                                fontWeight: 600,
                                marginTop: '10px'
                            }}>
                                Cluster Distributor (Our Model)
                            </h3>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0
                            }}>
                                {[
                                    { icon: 'dollar', label: 'Same ₹50 Lakh', value: '→ 3-5 Products' },
                                    { icon: 'target', label: 'Smart Inventory', value: 'Shared across categories' },
                                    { icon: 'check', label: 'Risk', value: 'Low (diversified portfolio)' },
                                    { icon: 'network', label: 'Wide Reach', value: 'Architects, Builders, Designers, Govt. Tenders' },
                                    { icon: 'trending', label: 'Profit', value: 'Multi-stream income, higher success rate' }
                                ].map((item, idx) => (
                                    <li key={idx} style={{
                                        padding: '15px',
                                        marginBottom: '12px',
                                        background: 'rgba(0,0,0,0.2)',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '12px'
                                    }}>
                                        <span style={{ color: '#000', flexShrink: 0 }}>{renderIcon(item.icon, 24)}</span>
                                        <div>
                                            <strong style={{ color: '#000' }}>{item.label}:</strong>{' '}
                                            <span style={{ color: '#1a1a1a' }}>{item.value}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Product Range */}
                    <div style={{
                        padding: '40px',
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        borderRadius: '16px',
                        border: '1px solid rgba(201, 169, 110, 0.3)'
                    }}>
                        <h3 style={{
                            fontSize: '28px',
                            color: 'var(--accent-color)',
                            marginBottom: '25px',
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '15px'
                        }}>
                            {renderIcon('package', 28)} Product Range & Financial Advantages
                        </h3>
                        <p style={{
                            fontSize: '16px',
                            color: 'rgba(245, 241, 237, 0.9)',
                            marginBottom: '30px',
                            textAlign: 'center'
                        }}>
                            With the same ₹50 lakh investment, gain 3 to 5 product categories such as:
                        </p>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                            gap: '15px'
                        }}>
                            {productRange.map((product, idx) => (
                                <div key={idx} style={{
                                    padding: '15px 20px',
                                    background: 'rgba(201, 169, 110, 0.1)',
                                    borderRadius: '10px',
                                    border: '1px solid rgba(201, 169, 110, 0.3)',
                                    textAlign: 'center',
                                    color: 'var(--text-light)',
                                    fontSize: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}>
                                    {renderIcon('check', 16)} {product}
                                </div>
                            ))}
                        </div>
                        <p style={{
                            marginTop: '30px',
                            fontSize: '16px',
                            color: 'var(--accent-color)',
                            textAlign: 'center',
                            fontStyle: 'italic'
                        }}>
                            👍 This model reduces dependency on a single product and opens a diverse, multi-category 
                            market entry with lower risk and higher returns.
                        </p>
                    </div>
                </div>
            </section>

            {/* Café Connections */}
            <section className="animate-section" style={{
                padding: '100px 60px',
                background: 'linear-gradient(180deg, #0A0A0A 0%, #0F0F0F 100%)'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                        gap: '50px',
                        alignItems: 'center'
                    }}>
                        {/* Left Content */}
                        <div>
                            <h2 style={{
                                fontSize: 'clamp(36px, 5vw, 56px)',
                                fontWeight: 300,
                                color: '#fff',
                                marginBottom: '20px',
                                letterSpacing: '-1px'
                            }}>
                                Café Connections<br/>
                                Studio Connect Café
                            </h2>
                            <p style={{
                                fontSize: '18px',
                                color: 'rgba(245, 241, 237, 0.8)',
                                marginBottom: '30px',
                                lineHeight: 1.8
                            }}>
                                <strong>Where Ideas Brew, Designs Emerge, and Communities Connect</strong>
                            </p>

                            <div style={{
                                padding: '30px',
                                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                                borderRadius: '16px',
                                marginBottom: '30px',
                                border: '1px solid rgba(201, 169, 110, 0.2)'
                            }}>
                                <h3 style={{
                                    color: 'var(--accent-color)',
                                    fontSize: '22px',
                                    marginBottom: '15px'
                                }}>
                                    What is Café Connections?
                                </h3>
                                <p style={{
                                    color: 'rgba(245, 241, 237, 0.85)',
                                    fontSize: '16px',
                                    lineHeight: 1.7,
                                    marginBottom: '20px'
                                }}>
                                    Café Connections is more than a café — it is a platform for networking, learning, 
                                    and creative exchange.
                                </p>
                                <ul style={{
                                    listStyle: 'none',
                                    padding: 0,
                                    margin: 0,
                                    fontSize: '15px',
                                    color: 'rgba(245, 241, 237, 0.85)'
                                }}>
                                    <li style={{ marginBottom: '10px', paddingLeft: '20px', position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: 0, color: 'var(--accent-color)' }}>{renderIcon('mic', 20)}</span>
                                        A stage for seminars, workshops, and design talks.
                                    </li>
                                    <li style={{ marginBottom: '10px', paddingLeft: '20px', position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: 0, color: 'var(--accent-color)' }}>{renderIcon('palette', 20)}</span>
                                        An experience studio where professionals can explore, touch, and interact with diverse material solutions.
                                    </li>
                                    <li style={{ marginBottom: '10px', paddingLeft: '20px', position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: 0, color: 'var(--accent-color)' }}>{renderIcon('handshake', 20)}</span>
                                        A meeting point for architects, interior designers, institutes, and students to collaborate and grow together.
                                    </li>
                                </ul>
                            </div>

                            <div style={{
                                padding: '25px',
                                background: 'rgba(201, 169, 110, 0.1)',
                                borderRadius: '12px',
                                border: '1px solid rgba(201, 169, 110, 0.3)'
                            }}>
                                <h4 style={{
                                    color: 'var(--accent-color)',
                                    fontSize: '18px',
                                    marginBottom: '15px'
                                }}>
                                    Our Philosophy
                                </h4>
                                <p style={{
                                    color: 'rgba(245, 241, 237, 0.85)',
                                    fontSize: '15px',
                                    lineHeight: 1.7
                                }}>
                                    Café Connections is founded on three core pillars:<br/>
                                    <strong style={{ color: 'var(--accent-color)' }}>Fun</strong> <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '5px', marginRight: '5px' }}>{renderIcon('zap', 18)}</span> A vibrant, inspiring atmosphere that celebrates creativity.<br/>
                                    <strong style={{ color: 'var(--accent-color)' }}>Networking</strong> <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '5px', marginRight: '5px' }}>{renderIcon('handshake', 18)}</span> A collaborative hub connecting the design fraternity.<br/>
                                    <strong style={{ color: 'var(--accent-color)' }}>Education</strong> <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginLeft: '5px', marginRight: '5px' }}>{renderIcon('book', 18)}</span> A living classroom where global ideas meet practical applications.
                                </p>
                            </div>
                        </div>

                        {/* Right Image/Visual */}
                        <div style={{
                            padding: '40px',
                            background: 'linear-gradient(135deg, rgba(201, 169, 110, 0.2) 0%, rgba(201, 169, 110, 0.05) 100%)',
                            borderRadius: '20px',
                            border: '2px solid var(--accent-color)',
                            textAlign: 'center'
                        }}>
                            <div style={{
                                marginBottom: '20px',
                                color: 'var(--accent-color)',
                                display: 'flex',
                                justifyContent: 'center'
                            }}>
                                {renderIcon('coffee', 120)}
                            </div>
                            <h3 style={{
                                fontSize: '28px',
                                color: 'var(--accent-color)',
                                marginBottom: '20px',
                                fontWeight: 500
                            }}>
                                HAV'N'S CAFÉ
                            </h3>
                            <p style={{
                                fontSize: '18px',
                                color: 'rgba(245, 241, 237, 0.9)',
                                marginBottom: '30px',
                                lineHeight: 1.7
                            }}>
                                Launching the city's first exclusive architect cafe, a professional hub 
                                where design takes shape.
                            </p>
                            <div style={{
                                padding: '25px',
                                background: 'rgba(0,0,0,0.3)',
                                borderRadius: '12px',
                                marginBottom: '20px'
                            }}>
                                <p style={{
                                    fontSize: '16px',
                                    color: 'rgba(245, 241, 237, 0.85)',
                                    marginBottom: '15px'
                                }}>
                                    This is your space to work, meet clients, and create with our complete material library at your fingertips.
                                </p>
                                <p style={{
                                    fontSize: '20px',
                                    color: 'var(--accent-color)',
                                    fontWeight: 600
                                }}>
                                    NO CHARGES APPLIED
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Features */}
                    <div style={{
                        marginTop: '60px',
                        padding: '40px',
                        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
                        borderRadius: '16px',
                        border: '1px solid rgba(201, 169, 110, 0.3)'
                    }}>
                        <h3 style={{
                            fontSize: '28px',
                            color: 'var(--accent-color)',
                            marginBottom: '30px',
                            textAlign: 'center'
                        }}>
                            Features & Enhancements of the Café
                        </h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: '20px'
                        }}>
                            {[
                                { icon: 'book', title: 'Design Library', desc: 'An archive of books, journals, and global references on interiors, architecture, and art.', image: PLACEHOLDER_IMAGES.design },
                                { icon: 'monitor', title: 'Digital Panels & VR', desc: 'Interactive displays to explore projects and design catalogs.', image: PLACEHOLDER_IMAGES.planning },
                                { icon: 'palette', title: 'Material Experience Corners', desc: 'A hands-on showcase of laminates, veneers, flooring, textiles, and finishes.', image: PLACEHOLDER_IMAGES.materials },
                                { icon: 'message', title: 'Talk Corners', desc: 'Dedicated zones for intimate seminars, discussions, and networking meets.', image: PLACEHOLDER_IMAGES.team },
                                { icon: 'coffee', title: 'Coffee & Conversations Lounge', desc: 'A relaxed environment for creative dialogue.', image: PLACEHOLDER_IMAGES.consultation },
                                { icon: 'art', title: 'Art & Design Showcase', desc: 'Rotating exhibits featuring professional works and student projects.', image: PLACEHOLDER_IMAGES.office }
                            ].map((feature, idx) => (
                                <div key={idx} style={{
                                    background: 'rgba(201, 169, 110, 0.05)',
                                    borderRadius: '12px',
                                    border: '1px solid rgba(201, 169, 110, 0.2)',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer'
                                }}
                                className="cafe-feature-card"
                                >
                                    {/* Front Image */}
                                    <div style={{
                                        height: '180px',
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}>
                                        <img
                                            src={feature.image}
                                            alt={feature.title}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.3s ease'
                                            }}
                                            className="cafe-feature-image"
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '12px',
                                            fontSize: '24px',
                                            background: 'rgba(0, 0, 0, 0.6)',
                                            borderRadius: '50%',
                                            width: '40px',
                                            height: '40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backdropFilter: 'blur(10px)',
                                            color: 'var(--accent-color)'
                                        }}>
                                            {renderIcon(feature.icon, 32)}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div style={{ padding: '20px' }}>
                                        <h4 style={{
                                            fontSize: '18px',
                                            color: 'var(--accent-color)',
                                            marginBottom: '10px'
                                        }}>
                                            {feature.title}
                                        </h4>
                                        <p style={{
                                            fontSize: '14px',
                                            color: 'rgba(245, 241, 237, 0.85)',
                                            lineHeight: 1.6
                                        }}>
                                            {feature.desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section style={{
                padding: '120px 60px',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #0F0F0F 100%)',
                textAlign: 'center',
                borderTop: '2px solid var(--accent-color)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '600px',
                    height: '600px',
                    background: 'radial-gradient(circle, rgba(201, 169, 110, 0.05) 0%, transparent 70%)',
                    borderRadius: '50%',
                    filter: 'blur(80px)'
                }}></div>

                <div style={{ maxWidth: '900px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <h2 style={{
                        fontSize: 'clamp(36px, 5vw, 64px)',
                        fontWeight: 300,
                        color: 'var(--text-light)',
                        marginBottom: '25px',
                        letterSpacing: '-1px'
                    }}>
                        Be Part of a Winning<br/>
                        <span style={{ color: 'var(--accent-color)' }}>Ecosystem</span>
                    </h2>
                    <p style={{
                        fontSize: '18px',
                        color: 'rgba(245, 241, 237, 0.7)',
                        marginBottom: '45px',
                        lineHeight: 1.8
                    }}>
                        Where innovation meets opportunity. Join us as a distributor, cluster distributor, 
                        franchise, or dealer and gain access to exclusive rights, premium support, and a proven business model.
                    </p>
                    <div style={{
                        display: 'flex',
                        gap: '20px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <Link to="/contact" style={{
                            display: 'inline-block',
                            padding: '20px 50px',
                            background: 'var(--accent-color)',
                            color: '#000',
                            textDecoration: 'none',
                            borderRadius: '50px',
                            fontSize: '16px',
                            fontWeight: 600,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            transition: 'all 0.3s ease',
                            border: '2px solid var(--accent-color)',
                            boxShadow: '0 10px 30px rgba(201, 169, 110, 0.3)'
                        }}
                        className="cta-btn-primary"
                        >
                            Become a Partner
                        </Link>
                        <Link to="/segments" style={{
                            display: 'inline-block',
                            padding: '20px 50px',
                            background: 'transparent',
                            color: 'var(--accent-color)',
                            textDecoration: 'none',
                            borderRadius: '50px',
                            fontSize: '16px',
                            fontWeight: 600,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            transition: 'all 0.3s ease',
                            border: '2px solid var(--accent-color)'
                        }}
                        className="cta-btn-secondary"
                        >
                            View Segments
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer style={{
                background: '#0A0A0A',
                padding: '40px 60px',
                textAlign: 'center',
                borderTop: '1px solid rgba(201, 169, 110, 0.2)'
            }}>
                <img src={`${process.env.PUBLIC_URL}/current-logo.png`} alt="Hav'n Interiors" style={{
                    height: '50px',
                    marginBottom: '20px',
                    opacity: 0.9
                }} />
                <p style={{
                    color: 'rgba(245, 241, 237, 0.6)',
                    fontSize: '14px',
                    margin: 0
                }}>
                    &copy; 2025 Hav'n Interiors & Global Sourcing. All Rights Reserved.
                </p>
                <p style={{
                    marginTop: '10px',
                    fontSize: '12px',
                    color: 'rgba(245, 241, 237, 0.4)'
                }}>
                    Innovate. Integrate. Execute.
                </p>
            </footer>

            {/* Hover Effects CSS */}
            <style jsx>{`
                .partner-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5);
                }

                .category-card:hover {
                    transform: translateY(-5px);
                    border-color: var(--accent-color) !important;
                    box-shadow: 0 10px 30px rgba(201, 169, 110, 0.2);
                }

                .cta-btn-primary:hover {
                    background: transparent !important;
                    color: var(--accent-color) !important;
                    transform: translateY(-3px);
                }

                .cta-btn-secondary:hover {
                    background: var(--accent-color) !important;
                    color: #000 !important;
                    transform: translateY(-3px);
                }

                .cafe-feature-card:hover {
                    transform: translateY(-5px);
                    border-color: var(--accent-color) !important;
                    box-shadow: 0 10px 30px rgba(201, 169, 110, 0.2);
                }

                .cafe-feature-card:hover .cafe-feature-image {
                    transform: scale(1.1);
                }
            `}</style>
        </div>
    );
};
 

export default PartnershipPage;

